import {
  downloadAsFile,
  exportHtmlAsDocx,
  exportHtmlAsMarkdown,
  exportHtmlAsPdf,
  exportMarkdownAsPdf,
  exportPlainTextAsPdf,
  exportToDocx,
  markdownToHtml,
} from "@/utils/markdownExport";
import type { FileRecord } from "@cohesive/api-client";
import type { FileRecordContentKind } from "./contentType";

export type FileExportKind =
  | "html"
  | "pdf"
  | "markdown"
  | "docx"
  | "text"
  | "original";

export type FileRecordExportMenuEntry = {
  format: FileExportKind;
  label: string;
};

export {
  FILE_EXPORT_FORMAT_CATALOG,
  FILE_EXPORT_VARIANTS_BY_CONTENT_KIND,
  exportVariantsForContentKind as menuEntriesForKind,
} from "@/object-actions/fileRecordExportCatalog";

export async function fetchFileRecordTextBody(
  record: FileRecord,
): Promise<string> {
  if (!record.url) {
    throw new Error("File URL not available");
  }
  const res = await fetch(record.url);
  if (!res.ok) {
    throw new Error(`Failed to load file (${res.status})`);
  }
  return res.text();
}

/**
 * Runs export for markdown/html/text using an already-fetched body (e.g. from
 * {@link fetchFileRecordTextBody}). Does not handle `original` — callers use
 * {@link useFileRecordDownload} for that.
 */
export async function runExportFromFetchedBody(params: {
  kind: FileRecordContentKind;
  format: FileExportKind;
  baseName: string;
  body: string;
}): Promise<void> {
  const { kind, format, baseName, body } = params;
  if (format === "original") {
    throw new Error("original export must use download()");
  }

  if (kind === "markdown") {
    const md = body;
    if (format === "html") {
      const html = await markdownToHtml(md);
      downloadAsFile(html, `${baseName}.html`, "text/html");
    } else if (format === "markdown") {
      downloadAsFile(md, `${baseName}.md`, "text/markdown");
    } else if (format === "pdf") {
      await exportMarkdownAsPdf(md, baseName);
    } else if (format === "docx") {
      await exportToDocx(md, baseName);
    }
    return;
  }

  if (kind === "html") {
    const html = body;
    if (format === "html") {
      downloadAsFile(html, `${baseName}.html`, "text/html");
    } else if (format === "pdf") {
      exportHtmlAsPdf(html, baseName);
    } else if (format === "markdown") {
      exportHtmlAsMarkdown(html, baseName);
    } else if (format === "docx") {
      await exportHtmlAsDocx(html, baseName);
    }
    return;
  }

  if (kind === "text") {
    const text = body;
    if (format === "text") {
      downloadAsFile(text, `${baseName}.txt`, "text/plain");
    } else if (format === "pdf") {
      exportPlainTextAsPdf(text, baseName);
    }
    return;
  }

  throw new Error("Export not supported for this file type");
}
